import type React from 'react';
import { cn } from '@/uptk/core/lib/utils';

export enum SpinnerSizes {
  xs = 16,
  sm = 24,
  md = 32,
  lg = 48,
}

export enum SpinnerDisplayTypes {
  block = 'block',
  inline = 'inline',
  overlay = 'overlay',
}

export interface SpinnerProps {
  children?: React.ReactNode;
  display?: keyof typeof SpinnerDisplayTypes | string | number;
  loading?: boolean;
  size?: keyof typeof SpinnerSizes;
  className?: string;
  color1?: string; // Primary color (kept for backward compatibility)
  color2?: string; // Unused in circular spinner
  color3?: string; // Unused in circular spinner
  variant?: 'circular' | 'dots'; // Spinner style variant
}

/**
 * Spinner component to replace SpectrumLoader
 *
 * Modern Microsoft-style circular spinner with smooth rotation.
 * Supports overlay, block, and inline display modes.
 * Includes both circular ring and dots variants.
 *
 * Usage:
 * ```tsx
 * <Spinner loading={true} size="md" display="overlay" variant="circular">
 *   <YourContent />
 * </Spinner>
 * ```
 */
export const Spinner: React.FC<SpinnerProps> = ({
  children,
  display = 'block',
  loading = true,
  size = 'md',
  className,
  color1 = 'hsl(var(--portal-accent))',
  color2,
  color3,
  variant = 'circular',
  ...rest
}) => {
  const spinnerSize = SpinnerSizes[size as keyof typeof SpinnerSizes];

  if (!loading) {
    return <>{children}</>;
  }

  const isOverlay = display === 'overlay';
  const isInline = display === 'inline';

  const renderCircularSpinner = () => (
    <svg
      className="animate-spin"
      width={spinnerSize}
      height={spinnerSize}
      viewBox="0 0 50 50"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        className="opacity-25"
        cx="25"
        cy="25"
        r="20"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <circle
        className="opacity-75"
        cx="25"
        cy="25"
        r="20"
        stroke="var(--spinner-color)"
        strokeWidth="4"
        fill="none"
        strokeDasharray="80 60"
        strokeLinecap="round"
      />
    </svg>
  );

  const renderDotsSpinner = () => {
    const dotSize = spinnerSize / 4;
    return (
      <div
        className="flex gap-2"
        style={
          {
            '--spinner-dot-size': `${dotSize}px`,
          } as React.CSSProperties
        }
      >
        <span
          className="inline-block h-[var(--spinner-dot-size)] w-[var(--spinner-dot-size)] rounded-full bg-[var(--spinner-color)] animate-bounce [animation-duration:1.4s] [animation-delay:0s]"
        />
        <span
          className="inline-block h-[var(--spinner-dot-size)] w-[var(--spinner-dot-size)] rounded-full bg-[var(--spinner-color)] animate-bounce [animation-duration:1.4s] [animation-delay:0.2s]"
        />
        <span
          className="inline-block h-[var(--spinner-dot-size)] w-[var(--spinner-dot-size)] rounded-full bg-[var(--spinner-color)] animate-bounce [animation-duration:1.4s] [animation-delay:0.4s]"
        />
      </div>
    );
  };

  return (
    <span
      className={cn(
        'relative',
        isInline ? 'inline' : 'block',
        'h-full',
        className
      )}
      {...rest}
    >
      {/* Overlay backdrop */}
      {isOverlay && loading && (
        <div className="absolute inset-0 bg-black/50 dark:bg-black/70 z-[4]" />
      )}

      {children}

      {/* Loader */}
      {loading && (
        <div
          className={cn(
            'flex justify-center items-center',
            isOverlay ? 'absolute inset-0 m-auto' : 'static',
            'h-[100px] z-[5]'
          )}
          style={{ '--spinner-color': color1 } as React.CSSProperties}
        >
          {variant === 'circular' ? renderCircularSpinner() : renderDotsSpinner()}
        </div>
      )}
    </span>
  );
};

// Keep SpectrumLoader name for backward compatibility
export const SpectrumLoader = Spinner;

export default Spinner;
